import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { Timepicker } from "timepicker-ui-react";
import "timepicker-ui/main.css";
import {
  TRAINING_LIST,
  TRAINING_UPDATE,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
} from "../../routes/routes";
import { NoDataFound, Pagination, ValidationMessage, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import Loader from "../../components/common/Loader";
import StickyTable from "../../components/common/StickyTable";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "in_progress", label: "In progress" },
  { value: "past", label: "Past" },
];

function dateBadge(row) {
  if (row.timing === "past") return { text: "Past", className: "badge bg-label-secondary" };
  if (row.days_until === 0) return { text: "Today", className: "badge bg-label-success" };
  return { text: `In ${row.days_until} days`, className: "badge bg-label-warning" };
}

const TrainingList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [paginator, setPaginator] = useState({});
  const [filter, setFilter] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [showClearBtn, setShowClearBtn] = useState(false);
  const dateRef = useRef(null);
  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);
  const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

  const handleStateClear = () => {
    selectStateRef.current?.clearValue();
    selectCityRef.current?.clearValue();
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };
  const handleCityClear = () => {
    selectCityRef.current?.clearValue();
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };
  const handleRegionClear = () => {
    selectRegionRef.current?.clearValue();
    selectChapterRef.current?.clearValue();
  };
  const handleChapterClear = () => {
    selectChapterRef.current?.clearValue();
  };
  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    if (dateRef.current?.flatpickr) dateRef.current.flatpickr.setDate([]);
    setFilter({ ...filter, from_date: "", to_date: "" });
  };

  const openEdit = (row) => {
    setEditErrors({});
    const current = moment(row.training_date, "DD/MM/YYYY");
    setEditData({
      id: row.id,
      chapter_name: row.chapter_name,
      month_label: current.format("MMMM YYYY"),
      min_date: moment.max(moment().startOf("day"), current.clone().startOf("month")).toDate(),
      max_date: current.clone().endOf("month").toDate(),
      training_date: current.format("DD/MM/YYYY"),
      training_time: moment(row.training_time, ["hh:mm a", "HH:mm:ss"]).format("hh:mm A"),
    });
    setEditModal(true);
  };

  const saveEdit = () => {
    setSaving(true);
    setEditErrors({});
    const requestBody = {
      training_date: editData?.training_date,
      training_time: editData?.training_time
        ? moment(editData.training_time, ["hh:mm A", "h:mm A", "HH:mm:ss", "HH:mm"]).format("HH:mm:ss")
        : "",
    };
    axios.put(TRAINING_UPDATE + editData.id, requestBody)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          toast.success(response.data.msg || "Training updated successfully");
          setEditModal(false);
          setFilter((prev) => ({ ...prev }));
        }
      })
      .catch((err) => {
        if (err.response?.status === 422 && err.response.data?.error && !Array.isArray(err.response.data.error)) {
          setEditErrors(err.response.data.error);
        }
        toast.error(err.response?.data?.msg || "Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  useEffect(() => {
    setLoading(true);
    axios.get(TRAINING_LIST, { params: { ...filter, page, perPage } })
      .then((response) => {
        setRows(response.data.data.trainings || []);
        setPaginator({
          current: response.data.data.current_page,
          last: response.data.data.last_page,
          total: response.data.data.total,
        });
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filter, page, perPage]);

  useEffect(() => {
    const load = async () => {
      const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN, { params: filter });
      setCountryOptions(countryResponse.data.data || []);
      const stateResponse = await axios.get(STATE_LIST_DROPDOWN, { params: filter });
      setStateOptions(stateResponse.data.data || []);
      const cityResponse = await axios.get(CITY_LIST_DROPDOWN, { params: filter });
      setCityOptions(cityResponse.data.data || []);
      const regionResponse = await axios.get(REGION_LIST_DROPDOWN, { params: filter });
      setRegionOptions(regionResponse.data.data || []);
      const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, { params: filter });
      setChapterOptions(chapterResponse.data.data || []);
    };
    load().catch(() => {});
  }, [filter]);

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Training</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to="/dashboard">Dashboard</Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#212b39" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Training</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <Link to="/reports/training" className="btn rounded-pill btn-primary ibutton">
                  <span className="tf-icons bx bxs-report"></span>&nbsp; Training Reports
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="container-fluid p-0">
          <div className="card icard sticky-table-card">
            <div className="container-fluid f_containter">
              <div className="row mb-4">
                {hasRole([1]) && (
                  <div className="mb-3 col-md-3">
                    <label className="form-label" htmlFor="country_id">Country</label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      name="country_id"
                      isClearable
                      options={countryOptions}
                      onChange={(selected) => {
                        handleStateClear();
                        setFilter({
                          ...filter,
                          country_id: selected?.value,
                          state_id: null,
                          city_id: null,
                          region_id: null,
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                )}
                {hasRole([1, 6]) && (
                  <div className="mb-3 col-md-3">
                    <label className="form-label" htmlFor="state_id">State</label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      ref={selectStateRef}
                      name="state_id"
                      isClearable
                      options={stateOptions}
                      onChange={(selected) => {
                        handleCityClear();
                        setFilter({
                          ...filter,
                          state_id: selected?.value,
                          city_id: null,
                          region_id: null,
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                )}
                {hasRole([1, 6, 7]) && (
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="city_id">City</label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      ref={selectCityRef}
                      name="city_id"
                      isClearable
                      options={cityOptions}
                      onChange={(selected) => {
                        handleRegionClear();
                        setFilter({
                          ...filter,
                          city_id: selected?.value,
                          region_id: null,
                          chapter_id: null,
                        });
                      }}
                    />
                  </div>
                )}
                {hasRole([1, 6, 7, 8]) && (
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="region_id">Region</label>
                      <Select
                        ref={selectRegionRef}
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                        name="region_id"
                        isClearable
                        options={regionOptions}
                        onChange={(selected) => {
                          handleChapterClear();
                          setFilter({
                            ...filter,
                            region_id: selected?.value,
                            chapter_id: null,
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
                {hasRole([1, 6, 7, 8, 2]) && (
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="chapter_id">Circle</label>
                      <Select
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                        ref={selectChapterRef}
                        name="chapter_id"
                        isClearable
                        options={chapterOptions}
                        onChange={(selected) => setFilter({ ...filter, chapter_id: selected?.value })}
                      />
                    </div>
                  </div>
                )}
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="status">Status</label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      name="status"
                      isClearable
                      options={statusOptions}
                      onChange={(selected) => setFilter({
                        ...filter,
                        status: selected !== null && selected !== undefined ? selected.value : null,
                      })}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3 dateRangeDiv">
                    <label className="form-label">Date Range</label>
                    <div className="datepicker_wrapper">
                      <div className="datepicker">
                        <Flatpickr
                          ref={dateRef}
                          className="form-control"
                          options={{
                            mode: "range",
                            dateFormat: "d/m/Y",
                            onChange: (selected) => {
                              setShowClearBtn(true);
                              if (selected[0] !== undefined && selected[1] !== undefined && selected[1] !== "") {
                                setFilter({
                                  ...filter,
                                  from_date: moment(selected[0]).format("DD/MM/YYYY"),
                                  to_date: moment(selected[1]).format("DD/MM/YYYY"),
                                });
                              }
                            },
                          }}
                          placeholder="Select date range"
                        />
                        {showClearBtn && (
                          <button type="button" className="border-0 clearDateRangeBtn" title="Clear" onClick={handleDateRangeClear}>
                            <i className="bx bx-x"></i>
                          </button>
                        )}
                      </div>
                      <div className="cal_icon" onClick={() => dateRef.current?.flatpickr?.open()}>
                        <span className="input-group-text calendar-icon"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <StickyTable refreshDeps={[rows, loading]}>
              <table className="table table-striped itable">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>Circle</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Attended</th>
                    <th>Absent</th>
                    <th>Not marked</th>
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody className="table-border-bottom-0">
                  {loading ? (
                    <Loader loading={loading} colspan="8" />
                  ) : rows.length > 0 ? (
                    rows.map((row) => {
                    const badge = dateBadge(row);
                    return (
                      <tr key={row.id}>
                        <td>{row.sno}</td>
                        <td>
                          {row.chapter_name}
                          {(row.city_name || row.state_name) && (
                            <div className="text-muted">{[row.city_name, row.state_name].filter(Boolean).join(", ")}</div>
                          )}
                        </td>
                        <td>
                          {row.training_date}{" "}
                          <span className={badge.className}>{badge.text}</span>
                          {row.is_rescheduled === 1 && (
                            <span className="badge bg-label-info ms-1" title="Date changed manually">Rescheduled</span>
                          )}
                        </td>
                        <td>{row.training_time || "-"}</td>
                        <td><span className="badge bg-label-success">{row.attended_count}</span></td>
                        <td><span className="badge bg-label-danger">{row.absent_count}</span></td>
                        <td><span className="badge bg-label-warning">{row.unmarked_count}</span></td>
                        <td style={{ textAlign: "center" }}>
                          {hasPermission("training-edit") && row.can_edit && (
                            <button
                              type="button"
                              className="badge bg-label-success me-1 border-0"
                              title="Edit Training Date & Time"
                              onClick={() => openEdit(row)}
                            >
                              <i className="bx bx-edit-alt"></i>
                            </button>
                          )}
                          <button
                            type="button"
                            className="badge bg-label-info me-1 border-0"
                            title="View"
                            onClick={() => navigate(`/trainings/${row.id}/attendance`)}
                          >
                            <i className="bx bx-show"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                  ) : (
                    <NoDataFound colspan="8" message="No Data Found" />
                  )}
                </tbody>
              </table>
            </StickyTable>
            <Pagination paginator={paginator} page={(no) => setPage(no)} perPage={perPage} setPerPage={setPerPage} />
          </div>
        </div>
      </div>

      <Modal
        show={editModal}
        size="l"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setEditModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter" className="modal_title1">
            Edit Training ({editData?.chapter_name})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="card-body py-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="training_date">
                    Training Date<span className="text-danger">*</span>
                  </label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      dateFormat: "d/m/Y",
                      enableTime: false,
                      minDate: editData?.min_date,
                      maxDate: editData?.max_date,
                    }}
                    onChange={(selected) =>
                      setEditData({
                        ...editData,
                        training_date: selected[0] ? moment(selected[0]).format("DD/MM/YYYY") : "",
                      })
                    }
                    value={editData?.training_date}
                    placeholder="Select Training Date"
                  />
                  <small className="text-muted">Date can be changed within {editData?.month_label} only.</small>
                  <ValidationMessage error={editErrors} name="training_date" />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="training_time_edit">
                    Training Time<span className="text-danger">*</span>
                  </label>
                  <Timepicker
                    id="training_time_edit"
                    className="form-control"
                    placeholder="Select Training Time"
                    options={{ clock: { type: "12h" }, ui: { clearButton: true } }}
                    defaultValue={editData?.training_time}
                    onConfirm={(data) => {
                      const formatted = moment(`${data?.hour}:${data?.minutes} ${data?.type}`, ["h:mm A", "HH:mm"]).format("HH:mm:ss");
                      setEditData({ ...editData, training_time: formatted });
                    }}
                    onClear={() => setEditData({ ...editData, training_time: "" })}
                  />
                  <ValidationMessage error={editErrors} name="training_time" />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-success isubmit_button" disabled={saving} onClick={saveEdit}>
            Update
          </button>
          <button type="button" className="btn btn-secondary ibutton" onClick={() => setEditModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TrainingList;
